import { fetcher } from 'utils/axios';

/**
 * ייחוס קמפיינים לנרשמים/נכנסים:
 * 1. `captureCampaignParamsFromUrl` רץ במסכי האורח (GuestGuard) — קולט printid/utm_* מה-URL
 *    ושומר ב-localStorage. נחיתה חדשה עם פרמטרים דורסת קליטה קודמת (הצד השרתי ממילא
 *    שומר רק רשומה ראשונה פר-משתמש).
 * 2. `submitCampaignAttribution` רץ פעם אחת אחרי כניסה מוצלחת (loadAppUserProfile) —
 *    שולח לשרת ומוחק מה-localStorage בהצלחה. מי שלא נכנס — לא נכתב ל-DB.
 */

const STORAGE_KEY = 'campaign-attribution';
/** נחיתה שלא הבשילה לכניסה תוך 30 יום — מתעלמים ונמחקת. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** שמות הפרמטרים ב-URL ← שמות השדות שנשלחים לשרת. */
const URL_PARAM_TO_FIELD = {
  printid: 'printId',
  utm_source: 'utmSource',
  utm_type: 'utmType',
  utm_campaign: 'utmCampaign',
} as const;

type CampaignAttribution = {
  printId?: string;
  utmSource?: string;
  utmType?: string;
  utmCampaign?: string;
  capturedAt: string;
};

export function captureCampaignParamsFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const captured: Partial<CampaignAttribution> = {};
    let hasAny = false;

    (Object.entries(URL_PARAM_TO_FIELD) as Array<[string, Exclude<keyof CampaignAttribution, 'capturedAt'>]>).forEach(
      ([param, field]) => {
        const value = params.get(param)?.trim();
        if (value) {
          captured[field] = value;
          hasAny = true;
        }
      },
    );

    if (!hasAny) return;

    const payload: CampaignAttribution = { ...captured, capturedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // קליטת קמפיין לעולם לא מפילה את מסך הלוגין
  }
}

export async function submitCampaignAttribution(): Promise<void> {
  let stored: CampaignAttribution | null = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    stored = JSON.parse(raw) as CampaignAttribution;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  if (!stored) return;

  const capturedAtMs = Date.parse(stored.capturedAt ?? '');
  if (!Number.isFinite(capturedAtMs) || Date.now() - capturedAtMs > MAX_AGE_MS) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }

  try {
    const res = (await fetcher('post', [
      '/api/users/campaign-attribution',
      {
        data: {
          printId: stored.printId,
          utmSource: stored.utmSource,
          utmType: stored.utmType,
          utmCampaign: stored.utmCampaign,
          capturedAt: stored.capturedAt,
        },
      },
    ], 'app')) as { result?: boolean } | null;

    // גם saved=false (כבר קיימת רשומה למשתמש — first-touch) נחשב הצלחה: אין מה לשלוח שוב.
    if (res?.result) {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // כשל רשת/שרת — משאירים ב-localStorage לניסיון בכניסה הבאה
  }
}
